import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          // Verify token is still valid by calling /auth/me
          const response = await authAPI.me();
          setUser(response.data);
          // Update stored user with fresh data
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          // Token is invalid or expired
          console.error('Token validation failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password, rememberMe = false, totpCode = null) => {
    try {
      const response = await authAPI.login({ 
        email, 
        password, 
        remember_me: rememberMe,
        totp_code: totpCode 
      });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setPendingLogin({ email, password, rememberMe });
        return { requires2FA: true };
      }
      
      const { access_token, user: userData } = response.data;
      
      // Store token and user
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setUser(userData);
      setRequires2FA(false);
      setPendingLogin(null);
      
      return { success: true, user: userData };
    } catch (error) {
      throw error;
    }
  }, []);

  const verify2FA = useCallback(async (totpCode) => {
    if (!pendingLogin) {
      throw new Error('No pending login');
    }
    
    return login(
      pendingLogin.email, 
      pendingLogin.password, 
      pendingLogin.rememberMe, 
      totpCode
    );
  }, [pendingLogin, login]);

  const cancel2FA = useCallback(() => {
    setRequires2FA(false);
    setPendingLogin(null);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setRequires2FA(false);
    setPendingLogin(null);
  }, []);

  const updateUser = useCallback((updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, [user]);

  const isMember = user?.role === 'user' && user?.user_type === 'member';
  const isExternal = user?.role === 'user' && user?.user_type === 'external';

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
    requires2FA,
    verify2FA,
    cancel2FA,
    isMember,
    isExternal,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
